/**
 * Surface a variant's manual `name` as the "Type" (style) option axis when a
 * product's variants are distinguished ONLY by name — i.e. the owner created
 * options like "asd1 / asd2" without turning on any "Options vary by" attribute
 * (Type / Color / Size all blank).
 *
 * Without this the storefront has nothing to build a picker from (its axes are
 * color/size/style), so it renders no selector and the customer can only ever
 * buy the first variant. Mapping name → style lets the existing Type picker —
 * on the product page AND the quick-add modal — drive the selection.
 *
 * Conservative by design — it is a NO-OP when:
 *   - there's a single variant (nothing to choose),
 *   - any structured attribute (color/size/style) is already set on any variant
 *     (the product is already using a real axis), or
 *   - the names don't actually differ (can't distinguish them anyway).
 */
export function applyNameAsTypeAxis(product) {
  const variants = product?.variants;
  if (!Array.isArray(variants) || variants.length <= 1) return product;

  const distinct = (key) =>
    new Set(variants.map((v) => String(v?.[key] ?? '').trim()).filter(Boolean));

  // Leave anything with a real structured axis completely alone.
  if (distinct('color').size > 0 || distinct('size').size > 0 || distinct('style').size > 0) {
    return product;
  }
  // Names must actually distinguish the variants.
  if (distinct('name').size <= 1) return product;

  return {
    ...product,
    variants: variants.map((v) => ({ ...v, style: v?.name ?? '' })),
  };
}
